// React Dependencies
import React, { useState } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { MapControls, OrthographicCamera } from '@react-three/drei';

// Internal Dependencies
import MapModelAllLevel from './MapModelAllLevel';
import { useMallMapStore } from '../../../hook/useMallMapStore';

const MainMap = ({ mallMapData, waypointEdges, waypointLabels, setCurrentLevel, onClickWaypoint, onAddWaypoint }) => {
  const [isMapControlEnabled, setIsMapControlEnabled] = useState(true);
  const { yEachMap } = useMallMapStore((state) => state);

  const getWaypointById = (id) => {
    for (let i = 0; i < mallMapData.length; i += 1) {
      for (let j = 0; j < mallMapData[i].waypoints.length; j += 1) {
        if (mallMapData[i].waypoints[j].id === id) {
          return { ...mallMapData[i].waypoints[j], mallMapIndex: i };
        }
      }
    }
  };

  const generateEdgePoint = (waypointA, waypointB) => {
    const points = [];
    /**
     * The "index" is used to set the "z" value for displaying mall maps vertically.
     * Setting the value of index to 0 would only display the edges of the first map.
     * By adding the index, we can show the waypoint edges according to different mall map waypoints.
     */
    const index = Math.max(waypointA.mallMapIndex, waypointB.mallMapIndex) || 0;

    /**
     * We need to see the zValue value, for the z-position
     * else the edges on the levels after first are not shown
     */
    const zValue = index === 0 ? 0.5 : 0.5 + ((index + 1) ** 2) - index;

    if (waypointA && waypointB) {
      points.push(new THREE.Vector3(waypointA.x, waypointA.y, zValue));
      points.push(new THREE.Vector3(waypointB.x, waypointB.y, zValue));
    }

    return points;
  };

  const generateGeometryEdges = (mallMapEdges) => {
    const edges = [];

    mallMapEdges.forEach((edge) => {
      edge.geometry = new THREE.BufferGeometry().setFromPoints(
        generateEdgePoint(getWaypointById(edge.waypointAId), getWaypointById(edge.waypointBId)),
      );

      edges.push(edge);
    });

    return edges;
  };

  return (
    <>
      <MapControls enableZoom makeDefault enabled={isMapControlEnabled} />
      <OrthographicCamera makeDefault zoom={10} near={1} far={500} position={[0, 20, 0]} />
      <ambientLight intensity={1.0} />
      <group>
        { _.map(mallMapData, (map, index) => (
          <MapModelAllLevel
            key={`map-${map.id}`}
            index={index}
            posY={index * yEachMap}
            mallMap={map}
            mallMapData={mallMapData}
            mallMapTexture={map.texture}
            mallMapEdges={generateGeometryEdges(waypointEdges, index)}
            mallMapLabels={waypointLabels}
            mallMapWaypoints={map.waypoints}
            onClickWaypoint={onClickWaypoint}
            onAddWaypoint={onAddWaypoint}
            setCurrentLevel={setCurrentLevel}
            setIsMapControlEnabled={setIsMapControlEnabled}
          />
        ))}
      </group>
    </>
  );
};

MainMap.propTypes = {
  mallMapData:     PropTypes.arrayOf(PropTypes.shape(Object)),
  waypointEdges:   PropTypes.arrayOf(PropTypes.shape(Object)),
  waypointLabels:  PropTypes.arrayOf(PropTypes.shape(Object)),
  onClickWaypoint: PropTypes.func,
  onAddWaypoint:   PropTypes.func.isRequired,
  setCurrentLevel: PropTypes.func.isRequired,
};

MainMap.defaultProps = {
  mallMapData:     [],
  waypointEdges:   [],
  waypointLabels:  [],
  onClickWaypoint: () => {},
};

export default MainMap;
